import { Logo } from './Logo';
import { useI18n } from '../i18n';

/**
 * In-app splash screen.
 *
 * Mirrors the Figma frame: white background, the EDF mark centered in a 96px
 * white rounded card with a soft `secondary`-tinted shadow, and "Welcome"
 * beneath it. Pixel-identical in layout to the boot shell in index.html, so
 * the handoff from that static markup to this component is seamless.
 *
 * Entrance is staggered: the logo card fades + scales in first,
 * then "Welcome" fades up. When `leaving` flips true, the whole overlay fades
 * out; `onExited` fires when that transition ends so the parent can unmount it.
 *
 */
export interface SplashProps {
  /** When true, fade the whole screen out. */
  leaving: boolean;
  /** Called once the fade-out transition completes. */
  onExited: () => void;
}

export function Splash({ leaving, onExited }: SplashProps) {
  const { t } = useI18n();

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={t('splash.loading')}
      onTransitionEnd={(e) => {
        // Only react to the overlay's own opacity transition, not children's.
        if (e.target === e.currentTarget && leaving) onExited();
      }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-white transition-opacity duration-500 ease-out"
      style={{ opacity: leaving ? 0 : 1 }}
    >
      <style>{`
        @keyframes edf-splash-pop {
          0%   { opacity: 0; transform: scale(0.92); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes edf-splash-rise {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .edf-splash-logo, .edf-splash-welcome {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
        }
      `}</style>

      <div className="flex -translate-y-[8%] flex-col items-center">
        {/* Logo card — fades + scales in first */}
        <div
          className="edf-splash-logo flex h-24 w-24 items-center justify-center rounded-3xl bg-white"
          style={{
            boxShadow: '0 10px 30px rgba(236, 132, 47, 0.2)',
            animation: 'edf-splash-pop 600ms cubic-bezier(0.16, 1, 0.3, 1) both',
          }}
        >
          <Logo width={56} height={21} />
        </div>

        {/* "Welcome" — rises in after the logo */}
        <p
          className="edf-splash-welcome mt-6 text-h3 font-semibold text-gray-900"
          style={{
            animation: 'edf-splash-rise 500ms ease-out 350ms both',
          }}
        >
          {t('splash.welcome')}
        </p>
      </div>
    </div>
  );
}